import { assert } from "@/lib/utils/assertions"

/**
 * A registry of consumers waiting for streamed responses by id — the
 * responses generated by the server's SlowResponseStreamer. A consumer waits
 * with waitForResponse(); the producer (the StreamedResponseFetcher) resolves
 * it with deliverIfWaiting().
 *
 * Responses are one-shot, so a delivery for an id nobody awaits is dropped,
 * never retained. Ready signals that arrive before their waiter are parked at
 * the signal level in the fetcher; payloads themselves are only ever fetched
 * on behalf of a live waiter.
 */
export class ResponseBuffer {
  // Bounds every wait: no streamed response may hang forever. Generous,
  // because slow blocks legitimately run for a minute or two — but it must
  // stay inside the server-side stash TTL (3 minutes) so a poll near the
  // deadline can still succeed.
  static DEFAULT_TIMEOUT_MS = 150_000

  responseCallbacks = new Map<string, PendingWait>()

  public async waitForResponse<T>(
    streamingResponseId: string,
    options: WaitOptions = {}
  ): Promise<T> {
    const { timeoutMs = ResponseBuffer.DEFAULT_TIMEOUT_MS, signal } = options
    if (signal?.aborted) {
      return Promise.reject(
        new Error(`canceled wait for streamed response ${streamingResponseId}`)
      )
    }

    let callbacks: ResponseCallbacks<T> | undefined

    const promise = new Promise<T>(
      (resolve, reject) => (callbacks = { resolve, reject })
    )
    assert(callbacks, "no callbacks")

    const timer = setTimeout(() => {
      this.forgetWait(streamingResponseId)
      callbacks?.reject(
        new Error(
          `timed out waiting for streamed response ${streamingResponseId}`
        )
      )
    }, timeoutMs)
    const onAbort = () => this.cancelWait(streamingResponseId)
    signal?.addEventListener?.("abort", onAbort)
    this.responseCallbacks.set(streamingResponseId, {
      callbacks: callbacks as ResponseCallbacks<unknown>,
      timer,
      removeAbortListener: signal
        ? () => signal.removeEventListener?.("abort", onAbort)
        : undefined,
    })

    return promise
  }

  // Withdraws a wait whose consumer is gone (aborted request, unmounted
  // component): the entry stops being polled and its deadline timer clears.
  cancelWait(streamingResponseId: string) {
    this.failWait(
      streamingResponseId,
      new Error(`canceled wait for streamed response ${streamingResponseId}`)
    )
  }

  // Rejects the waiting consumer: the response is never coming (the server
  // has no record of the id), so failing now beats polling out the deadline.
  failWait(streamingResponseId: string, error: Error) {
    const wait = this.forgetWait(streamingResponseId)
    wait?.callbacks.reject(error)
  }

  // Resolves the waiting consumer; a response nobody awaits is dropped, since
  // its one-shot id can never be claimed again.
  deliverIfWaiting<T>(data: StreamedResponse<T>) {
    const wait = this.forgetWait(data.id)
    if (!wait) return

    if (data.status === "success") {
      wait.callbacks.resolve(data.result)
    } else {
      wait.callbacks.reject(data.result)
    }
  }

  // The StreamedResponseFetcher polls these.
  waitingResponseIds(): string[] {
    return [...this.responseCallbacks.keys()]
  }

  isWaitingFor(streamingResponseId: string): boolean {
    return this.responseCallbacks.has(streamingResponseId)
  }

  // Settling the promise (or not) stays the caller's job.
  private forgetWait(streamingResponseId: string): PendingWait | undefined {
    const wait = this.responseCallbacks.get(streamingResponseId)
    if (!wait) return undefined

    clearTimeout(wait.timer)
    wait.removeAbortListener?.()
    this.responseCallbacks.delete(streamingResponseId)
    return wait
  }
}

export interface StreamedResponse<T> {
  id: string
  status: "success" | "error"
  result: T
}

interface WaitOptions {
  timeoutMs?: number
  signal?: AbortSignalLike
}

// The slice of AbortSignal the buffer uses; axios's GenericAbortSignal makes
// the listener methods optional, so ours are too.
export interface AbortSignalLike {
  aborted: boolean
  addEventListener?(type: "abort", listener: () => void): void
  removeEventListener?(type: "abort", listener: () => void): void
}

interface PendingWait {
  callbacks: ResponseCallbacks<unknown>
  timer: ReturnType<typeof setTimeout>
  removeAbortListener: (() => void) | undefined
}

interface ResponseCallbacks<T> {
  resolve: (value: T) => void
  reject: (value: unknown) => void
}
